import { DATE_FORMATS_FNS } from "components";
import { typedQueryClient, usePermanentRealTimeUser, useTypedMutation } from "hooks";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { ApiInstance } from "utils/Api";
import { getNameFromURL } from "utils/common";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { InterventionAttachment } from "models";

type AddAttachmentToInterventionRequest = {
  url: string;
  name: string;
  intervention_id: number;
  type: string;
  visible_to_customer: boolean;
};

export const useAddInterventionAttachmentMutation = () => {
  const { user } = usePermanentRealTimeUser();

  return useTypedMutation({
    mutationFn: async (payload: AddAttachmentToInterventionRequest) => (await ApiInstance.post("/interventions/attachment/add", payload)).data.intervention_attachment_id,
    onSuccess: (payload, intervention_attachment_id: number) => {
      const appointment = typedQueryClient.getQueryData(AppointmentDetailsKeys.view);
      if (!appointment?.interventions) return;

      const { interventions } = appointment;
      const interventionIdx = interventions.findIndex(({ id }) => id === payload.intervention_id);
      if (interventionIdx < 0) return;

      const existingAttachmentIdx = interventions[interventionIdx].attachments?.findIndex(a => a.id === intervention_attachment_id) ?? -1;
      if (existingAttachmentIdx >= 0) return;

      const newAttachment: InterventionAttachment = {
        id: intervention_attachment_id,
        url: payload.url,
        created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        name: getNameFromURL(payload.url),
        type: payload.type,
        intervention_id: payload.intervention_id,
        visible_to_customer: payload.visible_to_customer,
        user_id: user?.id || 0,
        user,
        is_final_car_check: false
      };

      typedQueryClient.setQueryData(AppointmentDetailsKeys.view, {
        ...appointment,
        interventions: appointment.interventions.with(interventionIdx, {
          ...interventions[interventionIdx],
          attachments: [...(interventions[interventionIdx].attachments ?? []), newAttachment]
        })
      });
    }
  });
};
